import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, sectionOf } from "@/lib/api";
import { LINK_KEYS } from "@/components/site/SocialLinks";
import { Field, FormDialog, RowActions, field } from "./FormBits";
import { ImageUpload } from "./ImageUpload";
import { MemberThumb, PhotoAlertBanner, PhotoWarning, photoProblem } from "./PhotoStatus";

const EMPTY = { name: "", role: "", bio: "", image_url: "", order: 0, section: "crew", links: {} };
const SECTIONS = [["founder", "Founders"], ["crew", "Crew"], ["host", "Hosts"]];

const LABEL = { founder: "founder", crew: "crew member", host: "host" };

export const MembersTab = ({ section = "crew" }) => {
  const [all, setAll] = useState([]);
  const [form, setForm] = useState(null);
  const [broken, setBroken] = useState({});
  const markBroken = useCallback((id) => setBroken((b) => (b[id] ? b : { ...b, [id]: true })), []);
  const rows = all.filter((m) => sectionOf(m) === section);
  const badCount = rows.filter((m) => ["broken", "outside"].includes(photoProblem(m, broken[m.id]))).length;
  const load = useCallback(() => { setBroken({}); api.get("/members").then((r) => setAll(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    const { id, ...body } = form;
    body.order = Number(body.order) || 0;
    body.links = Object.fromEntries(Object.entries(body.links || {}).filter(([, v]) => v));
    try {
      if (id) await api.put(`/admin/members/${id}`, body); else await api.post("/admin/members", body);
      toast.success("Saved"); setForm(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (m) => {
    if (!window.confirm(`Remove ${m.name}?`)) return;
    await api.delete(`/admin/members/${m.id}`).catch(() => {}); toast.success("Removed"); load();
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setLink = (k) => (e) => setForm({ ...form, links: { ...form.links, [k]: e.target.value } });

  return (
    <div>
      <button data-testid={`admin-add-${section}-btn`} onClick={() => setForm({ ...EMPTY, section, order: all.length })} className="mb-4 inline-flex items-center gap-2 bg-[#56ED4A] text-black px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] font-semibold"><Plus size={14} /> Add {LABEL[section]}</button>
      <PhotoAlertBanner count={badCount} testId={`admin-${section}-photo-alert`} />
      {!rows.length && <p data-testid={`admin-${section}-empty`} className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 mb-4">No {LABEL[section]}s yet. Tap “Add {LABEL[section]}” to add one.</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {rows.map((m) => (
          <div key={m.id} data-testid={`admin-member-${m.id}`} className="border border-white/10 bg-[#121216] p-4 flex gap-4 items-center">
            <MemberThumb m={m} broken={broken[m.id]} onBroken={markBroken} />
            <div className="flex-1 min-w-0">
              <div className="font-display uppercase text-xl truncate">{m.name}</div>
              <div className="font-mono text-xs text-zinc-400 truncate">{m.role} · <span className="text-[#56ED4A]">{(SECTIONS.find(([v]) => v === sectionOf(m)) || [])[1]}</span></div>
              <PhotoWarning problem={photoProblem(m, broken[m.id])} testId={`admin-member-${m.id}-photo-warning`} onFix={() => setForm({ ...m, section: sectionOf(m), links: { ...m.links } })} />
              <div className="mt-2"><RowActions testId={`admin-member-${m.id}`} onEdit={() => setForm({ ...m, section: sectionOf(m), links: { ...m.links } })} onDelete={() => del(m)} /></div>
            </div>
          </div>
        ))}
      </div>
      {form && (
        <FormDialog open onOpenChange={(o) => !o && setForm(null)} title={form.id ? `Edit ${LABEL[form.section] || "member"}` : `New ${LABEL[form.section] || "member"}`} onSubmit={save} testId="member-form">
          <Field label="Name"><input data-testid="member-form-name" required value={form.name} onChange={set("name")} className={field} /></Field>
          <Field label="Role"><input data-testid="member-form-role" required value={form.role} onChange={set("role")} className={field} /></Field>
          <div className="sm:col-span-2"><Field label="Photo"><ImageUpload value={form.image_url} onChange={(url) => setForm((f) => ({ ...f, image_url: url }))} testId="member-form-photo" /></Field></div>
          <Field label="Order"><input data-testid="member-form-order" type="number" value={form.order} onChange={set("order")} className={field} /></Field>
          <Field label="Show in section">
            <select data-testid="member-form-section" value={form.section} onChange={set("section")} className={`${field} bg-[#121216]`}>
              {SECTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <div className="sm:col-span-2"><Field label="Bio"><textarea data-testid="member-form-bio" rows={3} value={form.bio} onChange={set("bio")} className={field} /></Field></div>
          {LINK_KEYS.map((k) => (
            <Field key={k} label={`${k} link`}><input data-testid={`member-form-link-${k}`} value={form.links?.[k] || ""} onChange={setLink(k)} placeholder="https://" className={field} /></Field>
          ))}
        </FormDialog>
      )}
    </div>
  );
};
